import React from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';

import { Button, Label, LABEL_TYPE } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { XMarkIcon } from '@/resources';
import { SelectOptionProps } from '@/types';

export type FilterPopUpValues = {
  filterByOnlineStatus?: SingleValue<SelectOptionProps>;
  filterByEnabledStatus?: SingleValue<SelectOptionProps>;
  filterByOrganization?: SingleValue<SelectOptionProps>;
  filterByStatus?: SingleValue<SelectOptionProps>;
  filterByRole?: SingleValue<SelectOptionProps>;
  filterByDevice?: SingleValue<SelectOptionProps>;
  filterByType?: SingleValue<SelectOptionProps>;
};

export const Alignment = {
  right: 'right-0 md:right-[350px]',
  left: 'right-0'
};

type FilterModalLayoutProps = {
  children: React.ReactNode;
  onClose: () => void;
  reset: () => void;
  apply: (values: FilterPopUpValues) => void;
  values: FilterPopUpValues;
  alignment?: string;
  mode?: string;
  position?: { top: number; left: number };
  isDashboardPage?: boolean;
};

export const FilterModalLayout = ({
  children,
  onClose,
  reset,
  apply,
  values,
  alignment = Alignment.right,
  mode = 'modal',
  position,
  isDashboardPage = false
}: FilterModalLayoutProps) => {
  const [t] = useTranslation('common');

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (mode === 'dropdown') {
    return (
      <div
        className={`absolute left-0 top-full z-50 mt-2 w-80 rounded-2xl bg-white p-5 shadow-lg ${alignment}`}
        onClick={handleOverlayClick}>
        <div className='flex items-center justify-between rounded-t'>
          <h3 className='text-xl font-semibold text-primary-blue'>{t('filters')}</h3>
          <div onClick={onClose}>
            <XMarkIcon className='cursor-pointer font-light text-primary-black' />
          </div>
        </div>
        <div className='grid gap-4 py-4'>{children}</div>
        <div className='flex flex-row items-center justify-between'>
          <Label
            text={t('resetAll')}
            type={LABEL_TYPE.PARAGRAPH2}
            className='cursor-pointer text-primary-blue'
            onClick={reset}
          />
          <Button
            variant={ButtonVariant.PRIMARY}
            className='px-9 text-sm'
            handleOnClick={() => apply(values)}>
            {t('apply')}
          </Button>
        </div>
      </div>
    );
  }
  if (mode === 'modal' && isDashboardPage) {
    return (
      <div
        className='fixed z-50'
        style={{
          top: `${position?.top ?? 0}px`,
          left: `${position?.left ?? 0}px`
        }}>
        <div className='w-[420px] rounded-2xl bg-white p-5 shadow-lg'>
          <div className='flex items-center justify-between rounded-t'>
            <h3 className='text-xl font-semibold text-primary-blue'>{t('filters')}</h3>
            <div onClick={onClose}>
              <XMarkIcon className='cursor-pointer font-light text-primary-black' />
            </div>
          </div>
          <div className='grid gap-4 py-4'>{children}</div>
          <div className='flex flex-row items-center justify-between'>
            <Label
              text={t('resetAll')}
              type={LABEL_TYPE.PARAGRAPH2}
              className='cursor-pointer text-primary-blue'
              onClick={reset}
            />
            <Button
              variant={ButtonVariant.PRIMARY}
              className='px-9 text-sm'
              handleOnClick={() => apply(values)}>
              {t('apply')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className='absolute left-0 top-0 z-30 h-screen w-screen' onClick={handleOverlayClick}>
      <div
        className={`filter-pop-up-shadow fixed top-28 z-50 max-h-full w-full rounded-2xl bg-white p-5 md:top-20 md:w-1/3 ${alignment}`}>
        <div className='flex items-center justify-between rounded-t'>
          <h3 className='text-xl font-semibold text-primary-blue'>{t('filters')}</h3>
          <div onClick={onClose}>
            <XMarkIcon className='cursor-pointer font-light text-primary-black' />
          </div>
        </div>
        <div className='grid gap-4 py-4'>{children}</div>
        <div className='flex flex-row items-center justify-between'>
          <Label
            text={t('resetAll')}
            type={LABEL_TYPE.PARAGRAPH2}
            className='cursor-pointer text-primary-blue'
            onClick={reset}
          />
          <Button
            variant={ButtonVariant.PRIMARY}
            className='px-9 text-sm'
            handleOnClick={() => apply(values)}>
            {t('apply')}
          </Button>
        </div>
      </div>
    </div>
  );
};
